import { VideoPlatformClient } from "../generated-api";
import { ApiError } from "../generated-api/core/ApiError";
import { FetchHttpRequest } from "../generated-api/core/FetchHttpRequest";
import type { ApiRequestOptions } from "../generated-api/core/ApiRequestOptions";
import type { CancelablePromise } from "../generated-api/core/CancelablePromise";
import type { OpenAPIConfig } from "../generated-api/core/OpenAPI";
import { useAuthStore } from "../stores/authStore";
export type * from "../generated-api";

// Initialize the client
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8788";

// Pre-request guard: block non-GET requests when email is unverified.
// Auth endpoints (/authentication/*) are always allowed so resend/signout work.
class GuardedHttpRequest extends FetchHttpRequest {
  constructor(config: OpenAPIConfig) {
    super(config);
  }

  override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    const isAuthenticationEndpoint = options.url.startsWith("/authentication/");
    const isTermsAcceptanceUpdate =
      options.method === "PATCH" && options.url === "/users/profile";

    if (
      options.method !== "GET" &&
      !isAuthenticationEndpoint &&
      !isTermsAcceptanceUpdate
    ) {
      const user = useAuthStore.getState().user;
      if (user != null && user.emailVerified === false) {
        window.dispatchEvent(
          new CustomEvent("ui:toast", {
            detail: {
              message: "Please verify your email to perform this action.",
            },
          }),
        );
        throw new Error("Email not verified");
      }
    }
    return super.request<T>(options);
  }
}

const baseClient = new VideoPlatformClient(
  {
    BASE: API_URL,
    CREDENTIALS: "include",
    WITH_CREDENTIALS: true,
  },
  GuardedHttpRequest,
);

// Create a Proxy to intercept all API calls and handle 401 errors
function createApiInterceptor(
  client: VideoPlatformClient,
): VideoPlatformClient {
  return new Proxy(client, {
    get(target, prop) {
      const value = target[prop as keyof VideoPlatformClient];

      if (typeof value === "object" && value !== null) {
        // Intercept service objects (like client.videos, client.programs, etc.)
        return new Proxy(value, {
          get(serviceTarget, serviceProp) {
            const serviceValue =
              serviceTarget[serviceProp as keyof typeof serviceTarget];

            if (typeof serviceValue === "function") {
              // Wrap API methods
              return async function (...args: any[]) {
                try {
                  return await (serviceValue as Function).apply(
                    serviceTarget,
                    args,
                  );
                } catch (error) {
                  // Handle 401 Unauthorized errors
                  if (error instanceof ApiError && error.status === 401) {
                    console.log("401 Unauthorized - redirecting to login");

                    // Save current path for redirect after login
                    const currentPath =
                      window.location.pathname + window.location.search;
                    if (currentPath !== "/login") {
                      sessionStorage.setItem("redirectAfterLogin", currentPath);
                    }

                    // Clear user state and redirect to login
                    // We can't import useAuthStore here directly due to circular dependency
                    // So we dispatch a custom event that the app can listen to
                    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
                  }

                  // Re-throw the error for the component to handle
                  throw error;
                }
              };
            }

            return serviceValue;
          },
        });
      }

      return value;
    },
  });
}

export const client = createApiInterceptor(baseClient);
